import React from "react";

import Alert from "@busbud/design-system-components/dist/Alert";
import Box from "@busbud/design-system-components/dist/Box/Box";
import Dialog from "@busbud/design-system-components/dist/Dialog/Dialog";
import DialogContent from "@busbud/design-system-components/dist/DialogContent/DialogContent";
import DialogTitle from "@busbud/design-system-components/dist/DialogTitle/DialogTitle";
import Divider from "@busbud/design-system-components/dist/Divider";
import Text from "@busbud/design-system-components/dist/Text/Text";

import { ExpandableCard } from "@app/components/card-with-header/expandable-card";
import { Markdown } from "@app/components/text-formatting";
import { useAppContext } from "@app/helpers/hooks";

import { HepstarTermsCoveredChips } from "./hepstar-terms-chips/hepstar-terms-covered-chips";
import { HepstarTermsNotCoveredChips } from "./hepstar-terms-chips/hepstar-terms-not-covered-chips";
import { HepstarTermsUnderConditionsChips } from "./hepstar-terms-chips/hepstar-terms-under-conditions-chips";
import { useStyles } from "./hepstar-terms-conditions.styles";

interface Props {
  onClose?: () => void;
  cards_closed?: TCCards[];
}

export const TC_CARDS = {
  REFUNDABLE_ADDON: "refundable_addon",
  REQUEST_REFUND: "request_refund",
  UNDER_CONDITIONS: "under_conditions",
  REQUEST_STATUS: "request_status"
} as const;
export type TCCards = (typeof TC_CARDS)[keyof typeof TC_CARDS];

export const HepstarTermsAndConditions: React.FC<Props> = ({
  onClose,
  cards_closed = []
}) => {
  const { translator, device } = useAppContext();
  const { classes } = useStyles();

  const is_fullscreen = device.is_mobile && !device.is_tablet;

  const isDefaultClosed = (card: TCCards) => {
    return !cards_closed.includes(card);
  };

  const common_cards_props = {
    card_classes_override: { root: classes.cardNoBorder },
    card_header_classes_override: { root: classes.cardHeader },
    card_collapse_classes_override: {
      wrapperInner: classes.collapseInnerContent
    },
    show_divider: false
  };

  const _renderRefundableAddonCard = () => {
    return (
      <ExpandableCard
        header={translator.t(
          "!ancillary.hepstar.terms.refundable-addon.heading"
        )}
        is_default_expanded={isDefaultClosed(TC_CARDS.REFUNDABLE_ADDON)}
        {...common_cards_props}
      >
        <Box pb={3}>
          <Text>
            {translator.t("!ancillary.hepstar.terms.refundable-addon.text")}
          </Text>
        </Box>
      </ExpandableCard>
    );
  };

  const _renderRequestRefundCard = () => {
    return (
      <ExpandableCard
        header={translator.t("!ancillary.hepstar.terms.request-refund.heading")}
        is_default_expanded={isDefaultClosed(TC_CARDS.REQUEST_REFUND)}
        {...common_cards_props}
      >
        <Box pb={3}>
          <Alert severity="informative" size="sm">
            {translator.t("!ancillary.hepstar.terms.request-refund.disclaimer")}
          </Alert>
          <ul className={classes.requestList}>
            <li>
              <Text component="div">
                <Markdown className={classes.mdListItem}>
                  {translator.t(
                    "!ancillary.hepstar.terms.request-refund.text-1"
                  )}
                </Markdown>
              </Text>
            </li>
            <li>
              <Text>
                {translator.t("!ancillary.hepstar.terms.request-refund.text-2")}
              </Text>
            </li>
            <li>
              <Text>
                {translator.t("!ancillary.hepstar.terms.request-refund.text-3")}
              </Text>
            </li>
          </ul>
        </Box>
      </ExpandableCard>
    );
  };

  const _renderCoverageCard = () => {
    return (
      <ExpandableCard
        header={translator.t("!ancillary.hepstar.terms.covered.heading")}
        is_default_expanded={isDefaultClosed(TC_CARDS.UNDER_CONDITIONS)}
        {...common_cards_props}
      >
        <Box pb={3}>
          <Text>
            {translator.t("!ancillary.hepstar.terms.covered.subheading")}
          </Text>
          <HepstarTermsCoveredChips />
          <Text className={classes.coveredSubheading}>
            {translator.t(
              "!ancillary.hepstar.terms.under-conditions.subheading"
            )}
          </Text>
          <HepstarTermsUnderConditionsChips />
          <Text className={classes.coveredSubheading}>
            {translator.t("!ancillary.hepstar.terms.not-covered.subheading")}
          </Text>
          <HepstarTermsNotCoveredChips />
          <Markdown body_font_size="md" className={classes.coveredSubheading}>
            {translator.t("!ancillary.hepstar.terms.read-full.text")}
          </Markdown>
        </Box>
      </ExpandableCard>
    );
  };

  const _renderRequestStatusCard = () => {
    return (
      <ExpandableCard
        header={translator.t("!ancillary.hepstar.terms.request-status.heading")}
        {...common_cards_props}
        is_default_expanded={isDefaultClosed(TC_CARDS.REQUEST_STATUS)}
      >
        <Markdown body_font_size="md" className={classes.lastContent}>
          {translator.t("!ancillary.hepstar.terms.request-status.text")}
        </Markdown>
      </ExpandableCard>
    );
  };

  return (
    <Dialog
      open
      fullScreen={is_fullscreen}
      maxWidth="md"
      aria-labelledby="alert-dialog-title"
      onClose={onClose}
    >
      <DialogTitle classes={{ closeIcon: classes.closeIcon }} onClose={onClose}>
        {translator.t("!terms.metadata.title")}
      </DialogTitle>
      <Divider />
      <DialogContent classes={{ root: classes.dialogContent }}>
        {_renderRefundableAddonCard()}
        <Divider />
        {_renderRequestRefundCard()}
        <Divider />
        {_renderCoverageCard()}
        <Divider />
        {_renderRequestStatusCard()}
      </DialogContent>
    </Dialog>
  );
};
